import api from '@/services/api'
import type { ApiResponse } from '@/types/api.types'
import { formatStorageGb, usagePercent } from '@/utils/planLimit'

export interface TenantUsageLimits {
  max_users: number
  max_projects: number
  max_storage_gb: number
  /** Branch cap; absent only while an older API build is still live (see `buildUsageMetrics`). */
  max_branches?: number
}

export interface TenantPlanUsage {
  users: number
  projects: number
  storage_gb: number
  /** Branches in use; absent only against an older API build. */
  branches?: number
  limits: TenantUsageLimits
  /** Paid add-ons the tenant is entitled to, keyed by feature name (e.g. `digital_signature`). */
  features?: Record<string, boolean>
  portal_subscription_url: string
  portal_usage_url: string
  /** Add-on store: where extra capacity — branches included — is bought. */
  portal_addons_url?: string
}

/** Feature key of the Contract Digital Signature add-on. */
export const DIGITAL_SIGNATURE_FEATURE = 'digital_signature'

/** Feature key of the WhatsApp messaging add-on. */
export const WHATSAPP_FEATURE = 'whatsapp'

export function isFeatureEnabled(
  snapshot: TenantPlanUsage | null,
  feature: string
): boolean {
  return snapshot?.features?.[feature] === true
}

export interface UsageMetricRow {
  key: 'users' | 'projects' | 'storage' | 'branches'
  used: number
  max: number
  percent: number
  displayUsed: string
  displayMax: string
}

function unwrapUsage(payload: ApiResponse<TenantPlanUsage> | TenantPlanUsage): TenantPlanUsage {
  if ('data' in payload && payload.data) return payload.data
  return payload as TenantPlanUsage
}

export function buildUsageMetrics(snapshot: TenantPlanUsage): UsageMetricRow[] {
  const { users, projects, storage_gb, limits } = snapshot

  const rows: UsageMetricRow[] = [
    {
      key: 'users',
      used: users,
      max: limits.max_users,
      percent: usagePercent(users, limits.max_users),
      displayUsed: String(users),
      displayMax: String(limits.max_users),
    },
    {
      key: 'projects',
      used: projects,
      max: limits.max_projects,
      percent: usagePercent(projects, limits.max_projects),
      displayUsed: String(projects),
      displayMax: String(limits.max_projects),
    },
    {
      key: 'storage',
      used: storage_gb,
      max: limits.max_storage_gb,
      percent: usagePercent(storage_gb, limits.max_storage_gb),
      displayUsed: formatStorageGb(storage_gb),
      displayMax: String(limits.max_storage_gb),
    },
  ]

  // The branch metric is newer than the other three. During a deploy window the running
  // API may still omit it, and a bar reading "0 / 0" is worse than no bar at all — so the
  // row appears only once the cap is actually reported.
  if (limits.max_branches !== undefined) {
    const branches = snapshot.branches ?? 0
    rows.push({
      key: 'branches',
      used: branches,
      max: limits.max_branches,
      percent: usagePercent(branches, limits.max_branches),
      displayUsed: String(branches),
      displayMax: String(limits.max_branches),
    })
  }

  return rows
}

const planUsageService = {
  async getPlanUsage(): Promise<TenantPlanUsage> {
    const response = await api.get<ApiResponse<TenantPlanUsage> | TenantPlanUsage>(
      '/api/v1/tenant/plan-usage',
    )
    return unwrapUsage(response.data)
  },
}

export default planUsageService
